import { PromotionDetails, PromotionItem, usePromotion } from '@/lib/api/promotions'
import { useStore } from '@/lib/api/stores'
import { SearchPromotionInput } from '../search-promotion-input'
import { X, Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from "@/lib/utils"
import { getPromotionDiscount, formatDiscount } from '@/lib/promotion-discount'

interface PromotionsSelectorProps {
    storeId: string
    promotionIds: string[]
    onPromotionIdsChange: (ids: string[]) => void
}

/**
 * Component for selecting and managing promotions for an offer
 */
export const PromotionsSelector = ({
    storeId,
    promotionIds,
    onPromotionIdsChange,
}: PromotionsSelectorProps) => {
    const handleAddPromotion = (promotion: PromotionItem) => {
        if (!promotionIds.includes(promotion.id)) {
            onPromotionIdsChange([...promotionIds, promotion.id])
        }
    }

    const handleRemovePromotion = (promotionId: string) => {
        onPromotionIdsChange(promotionIds.filter((id) => id !== promotionId))
    }

    return (
        <div className="space-y-4">
            <SearchPromotionInput
                storeId={storeId}
                excludeIds={promotionIds}
                onSelect={handleAddPromotion}
            />
            {promotionIds.length > 0 && (
                <SelectedPromotionsList
                    storeId={storeId}
                    promotionIds={promotionIds}
                    onRemove={handleRemovePromotion}
                />
            )}
        </div>
    )
}

interface SelectedPromotionsListProps {
    storeId: string
    promotionIds: string[]
    onRemove: (id: string) => void
}

/**
 * Displays the list of selected promotions
 */
const SelectedPromotionsList = ({
    storeId,
    promotionIds,
    onRemove,
}: SelectedPromotionsListProps) => (
    <div className="space-y-2">
        {promotionIds.map((promotionId) => (
            <SelectedPromotionItem
                key={promotionId}
                storeId={storeId}
                promotionId={promotionId}
                onRemove={onRemove}
            />
        ))}
    </div>
)

interface SelectedPromotionItemProps {
    storeId: string
    promotionId: string
    onRemove: (id: string) => void
}

/**
 * Single selected promotion item with remove button
 */
const SelectedPromotionItem = ({
    storeId,
    promotionId,
    onRemove,
}: SelectedPromotionItemProps) => {
    const { data: promotion, isLoading } = usePromotion(promotionId, storeId)
    const { data: store } = useStore(storeId)

    if (isLoading) {
        return <LoadingPromotionItem />
    }

    if (!promotion) {
        return null
    }

    const discountLabel = formatDiscount(getPromotionDiscount(promotion.actions, store?.baseCurrency || 'USD'))

    return (
        <PromotionItemContent promotion={promotion} discountLabel={discountLabel} onRemove={onRemove} />
    )
}

interface PromotionItemContentProps {
    promotion: PromotionDetails
    discountLabel?: string | null
    onRemove: (id: string) => void
}

/**
 * Renders the promotion item content
 */
const PromotionItemContent = ({
    promotion,
    discountLabel,
    onRemove,
}: PromotionItemContentProps) => (
    <div
        className={cn(
            'flex items-center gap-3 rounded-lg border p-3',
            'bg-muted/50',
        )}
    >
        <div className="bg-primary/10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md">
            <Tag className="text-primary h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium">{promotion.name}</p>
                {discountLabel && (
                    <span className="flex-shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                        {discountLabel}
                    </span>
                )}
            </div>
            {promotion.code && (
                <p className="text-muted-foreground truncate text-xs">
                    Code: {promotion.code}
                </p>
            )}
        </div>
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={() => onRemove(promotion.id)}
        >
            <X className="h-4 w-4" />
        </Button>
    </div>
)

/**
 * Placeholder for a promotion still loading
 */
const LoadingPromotionItem = () => (
    <div className="flex animate-pulse items-center gap-3 rounded-lg border p-3">
        <div className="bg-muted h-8 w-8 rounded-md" />
        <div className="flex-1 space-y-2">
            <div className="bg-muted h-4 w-1/2 rounded" />
            <div className="bg-muted h-3 w-1/3 rounded" />
        </div>
    </div>
)

